/**
 * Adapter dùng chung cho các bước lưu qua `PUT /v1/knowledge` (B1, B2, B3).
 * Dữ liệu: các mục KienThuc gom theo loại (viTri). BE chỉ upsert/xoá các mục gửi lên.
 */
import { knowledgeApi } from '@/services/knowledge/knowledge-api';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import type { AiProposalItem } from '@/types/ai-agent';
import type { KienThucItem, KienThucLoai, KnowledgeModel } from '@/types/knowledge';
import type { RequestDTO } from '@/types/requestDTO';
import CommonJs from '@/utils/commonJs';
import { AdapterSaveError, type ApplyResult, type DiffRow, type StepAdapter } from './types';

export interface KnowledgeData {
  loaiByViTri: Record<number, string>;
  viTriByLoai: Record<string, number>;
  items: KienThucItem[];
}

export interface KnowledgeAdapterConfig {
  key: string;
  title: string;
  loadTenBuoc: string;
  loadViTris: number[];
  saveTenBuoc: string;
  saveViTris: number[];
  /** section engine → viTri */
  sections: Record<string, { viTri: number; label: string }>;
  /** Trích nội dung hiển thị/snapshot của một mục */
  textOf?: (item: KienThucItem) => string;
  /** Thông tin bổ sung cho snapshot (vd. điểm VRIO, liên kết SWOT) */
  extraOf?: (item: KienThucItem) => Record<string, unknown>;
  /** Ghi các trường đề xuất vào mục (mới hoặc bản sao mục cũ) */
  applyFields?: (item: KienThucItem, proposal: AiProposalItem, editedText: string | undefined) => KienThucItem;
  /** Tuỳ biến dòng diff (vd. bước chọn chiến lược) */
  describe?: (proposal: AiProposalItem, existing: KienThucItem | undefined) => Partial<DiffRow>;
  buildContext?: () => Promise<Record<string, unknown>>;
  /** Dữ liệu tham chiếu thêm vào snapshot (vd. danh sách SWOT có id để liên kết) */
  extraSnapshot?: (data: KnowledgeData) => Record<string, unknown>;
}

const tenantId = () => useAuthStore.getState().tenantID || '';
const requestHeader = () => usePrivateStore.getState().getRequestHeader();

export const loadKnowledge = async (tenBuoc: string, viTris: number[]): Promise<KnowledgeData> => {
  const header = requestHeader();
  if (CommonJs.checkNullIDSelectRequestHeader(header)) {
    throw new AdapterSaveError('Chưa chọn bộ phận / giai đoạn.');
  }
  const body: RequestDTO = { site: tenantId(), tenBuoc, viTris, requestHeader: header } as RequestDTO;
  const res = (await knowledgeApi.list(body)) as unknown as {
    success?: boolean;
    message?: string;
    data?: { listKienThucLoais?: KienThucLoai[]; listKienThucs?: KienThucItem[] };
  };
  if (!res || res.success === false || !res.data) {
    throw new AdapterSaveError(res?.message || 'Không tải được dữ liệu bước.');
  }
  const loaiByViTri: Record<number, string> = {};
  const viTriByLoai: Record<string, number> = {};
  (res.data.listKienThucLoais ?? []).forEach((l) => {
    loaiByViTri[l.viTri] = l.id;
    viTriByLoai[l.id] = l.viTri;
  });
  return { loaiByViTri, viTriByLoai, items: res.data.listKienThucs ?? [] };
};

const saveKnowledge = async (tenBuoc: string, viTris: number[], changed: KienThucItem[]) => {
  if (!changed.length) return;
  const model: KnowledgeModel = {
    ListKienThucs: changed,
    ListMucTieu: [],
    Site: tenantId(),
    ViTris: viTris,
    TenBuoc: tenBuoc,
    RequestHeader: requestHeader(),
  } as KnowledgeModel;
  const res = (await knowledgeApi.update('', model)) as unknown as { success?: boolean; message?: string };
  if (!res || res.success === false) {
    throw new AdapterSaveError(res?.message || 'Lưu dữ liệu thất bại.');
  }
};

let tempSeq = 0;
export const newKnowledgeItem = (loaiID: string, order: number): KienThucItem => ({
  id: `temp-ai-${Date.now()}-${++tempSeq}`,
  guid: 'temp',
  loaiID,
  noiDung: '',
  nguyenTac: '',
  ten: null,
  chon: false,
  thoigian: order,
  thoigians: 0,
  solan: 0,
  solans: 0,
  sogio: 0,
  sogios: 0,
});

const defaultText = (item: KienThucItem) => (item.noiDung ?? '').trim();

const CORE_KEYS: (keyof KienThucItem)[] = ['noiDung', 'nguyenTac', 'ten', 'chon', 'thoigian', 'loaiID'];
const coreOf = (item: KienThucItem) => JSON.stringify(CORE_KEYS.map((k) => item[k] ?? null));

/** Các thay đổi để đưa `current` về `target` (dùng cho hoàn tác). */
export const restoreChanges = (current: KienThucItem[], target: KienThucItem[]): KienThucItem[] => {
  const currentById = new Map(current.map((i) => [i.id, i]));
  const targetById = new Map(target.map((i) => [i.id, i]));
  const changes: KienThucItem[] = [];
  current.forEach((item) => {
    if (!targetById.has(item.id)) changes.push({ ...item, guid: 'delete' });
  });
  target.forEach((item, idx) => {
    const now = currentById.get(item.id);
    if (!now) {
      changes.push({ ...item, id: `temp-undo-${Date.now()}-${idx}`, guid: 'temp' });
    } else if (coreOf(now) !== coreOf(item)) {
      changes.push({ ...item, guid: 'update' });
    }
  });
  return changes;
};

/** Danh sách {id, text} các mục thuộc viTri (để làm ngữ cảnh/tham chiếu). */
export const itemsAt = (data: KnowledgeData, viTri: number) =>
  data.items
    .filter((i) => i.loaiID === data.loaiByViTri[viTri])
    .sort((a, b) => (a.thoigian ?? 0) - (b.thoigian ?? 0))
    .map((i) => ({ id: i.id, text: (i.noiDung ?? '').trim() }));

export const createKnowledgeAdapter = (cfg: KnowledgeAdapterConfig): StepAdapter<KnowledgeData> => {
  const textOf = cfg.textOf ?? defaultText;
  const viTriOfSection = (section: string) => cfg.sections[section]?.viTri;
  const sectionOfViTri = (viTri: number) =>
    Object.entries(cfg.sections).find(([, s]) => s.viTri === viTri)?.[0];
  const applyFields =
    cfg.applyFields ??
    ((item: KienThucItem, p: AiProposalItem, edited?: string) => ({
      ...item,
      noiDung: edited ?? String(p.fields.text ?? item.noiDung ?? ''),
    }));

  const inSections = (data: KnowledgeData) =>
    data.items.filter((i) => i.loaiID && sectionOfViTri(data.viTriByLoai[i.loaiID]) !== undefined);

  return {
    key: cfg.key,
    title: cfg.title,
    load: () => loadKnowledge(cfg.loadTenBuoc, cfg.loadViTris),
    buildContext: cfg.buildContext,
    buildSnapshot: (data) => {
      const sections: Record<string, unknown[]> = {};
      Object.keys(cfg.sections).forEach((s) => (sections[s] = []));
      inSections(data)
        .sort((a, b) => (a.thoigian ?? 0) - (b.thoigian ?? 0))
        .forEach((item) => {
          const section = sectionOfViTri(data.viTriByLoai[item.loaiID ?? '']);
          if (section) sections[section].push({ id: item.id, text: textOf(item), ...(cfg.extraOf?.(item) ?? {}) });
        });
      return { step: cfg.key, sections, ...(cfg.extraSnapshot?.(data) ?? {}) };
    },
    diff: (proposal, data) => {
      const byId = new Map(data.items.map((i) => [i.id, i]));
      return proposal.items.map((p) => {
        const existing = p.target.id ? byId.get(p.target.id) : undefined;
        const base: DiffRow = {
          ref: p.target.ref,
          op: p.op,
          sectionLabel: cfg.sections[p.target.section]?.label ?? p.target.section,
          before: existing ? textOf(existing) : '',
          after: p.op === 'delete' ? '' : String(p.fields.text ?? ''),
          rationale: p.rationale,
          editable: p.op !== 'delete',
          warning: p.op !== 'add' && !existing ? 'Mục này đã thay đổi hoặc bị xoá trên trang' : undefined,
        };
        return { ...base, ...(cfg.describe?.(p, existing) ?? {}) };
      });
    },
    apply: async (proposal, selectedRefs, edits): Promise<ApplyResult> => {
      const before = await loadKnowledge(cfg.loadTenBuoc, cfg.loadViTris);
      const byId = new Map(before.items.map((i) => [i.id, i]));
      const nextOrder: Record<string, number> = {};
      before.items.forEach((i) => {
        const k = i.loaiID ?? '';
        nextOrder[k] = Math.max(nextOrder[k] ?? 0, i.thoigian ?? 0);
      });
      const changed: KienThucItem[] = [];
      proposal.items
        .filter((p) => selectedRefs.includes(p.target.ref))
        .forEach((p) => {
          const edited = edits[p.target.ref];
          if (p.op === 'add') {
            const viTri = viTriOfSection(p.target.section);
            const loaiID = viTri !== undefined ? before.loaiByViTri[viTri] : undefined;
            if (!loaiID) throw new AdapterSaveError(`Không tìm thấy nhóm "${p.target.section}" trên trang.`);
            nextOrder[loaiID] = (nextOrder[loaiID] ?? 0) + 1;
            changed.push(applyFields(newKnowledgeItem(loaiID, nextOrder[loaiID]), p, edited));
            return;
          }
          const existing = byId.get(p.target.id);
          if (!existing) return; // đã bị xoá bởi người khác — bỏ qua
          if (p.op === 'delete') changed.push({ ...existing, guid: 'delete' });
          else changed.push({ ...applyFields({ ...existing }, p, edited), guid: 'update' });
        });
      await saveKnowledge(cfg.saveTenBuoc, cfg.saveViTris, changed);
      const after = await loadKnowledge(cfg.loadTenBuoc, cfg.loadViTris);
      const beforeIds = new Set(before.items.map((i) => i.id));
      const updatedIds = changed.filter((c) => c.guid === 'update').map((c) => c.id);
      const savedIds = [...after.items.filter((i) => !beforeIds.has(i.id)).map((i) => i.id), ...updatedIds];
      const scope = (d: KnowledgeData) => inSections(d);
      return {
        savedIds,
        saveEndpoint: `PUT /v1/knowledge ${cfg.saveTenBuoc}`,
        undo: async () => {
          const current = await loadKnowledge(cfg.loadTenBuoc, cfg.loadViTris);
          await saveKnowledge(cfg.saveTenBuoc, cfg.saveViTris, restoreChanges(scope(current), scope(before)));
        },
      };
    },
  };
};
